<template>
  <v-container fluid>
    <v-card :loading="loading">
      <v-card-title>
        <h2>
          <v-btn class="mr-2" icon text :to="{ name: 'usuarios-list' }">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="loading">Carregando...</span>
          <template v-else>
            <span v-if="geral.id === ''">Novo usuário</span>
            <span v-else>Alterar usuário: {{geral.login}} - {{geral.nome}}</span>
          </template>
        </h2>
      </v-card-title>
      <form @submit.prevent="submitForm">
        <v-tabs v-model="tab" grow>
          <v-tab>
            <v-icon
              left
              color="error"
              v-if="$v.geral.$anyDirty && $v.geral.$invalid"
              class="mdi mdi-alert-circle"
            ></v-icon>
            <span>Geral</span>
          </v-tab>
          <v-tab>Permissões</v-tab>
          <v-tab>Empresas</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card-text>
              <v-switch
                v-model="geral.ativo"
                label="Usuário ativo"
                hint="Atenção: usuários inativos não poderão acessar o sistema"
                persistent-hint
              />
              <v-text-field
                v-model="$v.geral.login.$model"
                v-touch="geral.login"
                :error-messages="loginErrors"
                :counter="30"
                label="Login"
                :disabled="geral.id !== ''"
              ></v-text-field>
              <v-text-field
                v-model="geral.nome"
                v-touch="geral.nome"
                :error-messages="nomeErrors"
                :counter="80"
                label="Nome"
              ></v-text-field>
              <v-text-field
                v-model="geral.email"
                v-touch="geral.email"
                :error-messages="emailErrors"
                :counter="128"
                label="Email"
              ></v-text-field>
              <v-autocomplete
                v-model="geral.id_funcionario"
                :items="funcionarios"
                :search-input.sync="pesquisa"
                no-filter
                :loading="pesquisando"
                item-value="id"
                label="Funcionário vinculado"
                clearable
                no-data-text="Nenhum funcionário encontrado"
                placeholder="Pesquise por nome ou número da matrícula"
                prepend-icon="mdi-database-search"
              >
                <template v-slot:item="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.matricula}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.matricula}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <template v-if="geral.id === ''">
                <v-text-field
                  :type="passwordType('senha')"
                  v-model="geral.senha"
                  :error-messages="senhaErrors"
                  v-touch="geral.senha"
                  :append-icon="passwordIcon('senha')"
                  label="Senha"
                  @click:append="
            hiddenPassword.senha = !hiddenPassword.senha
          "
                  hint="Atenção: o usuário deve mudar a senha no primeiro acesso por questões de segurança."
                  persistent-hint
                />
                <v-text-field
                  :type="passwordType('confirmacao_senha')"
                  v-model="geral.confirmacao_senha"
                  :error-messages="confirmacaoSenhaErrors"
                  v-touch="geral.confirmacao_senha"
                  :append-icon="passwordIcon('confirmacao_senha')"
                  label="Confirme a senha"
                  @click:append="
            hiddenPassword.confirmacao_senha = !hiddenPassword.confirmacao_senha
          "
                />
              </template>
              <template v-else>
                <v-checkbox label="Alterar senha" v-model="alterarSenha" />
                <v-text-field
                  v-if="alterarSenha"
                  :type="passwordType('senha')"
                  v-model="geral.senha"
                  :error-messages="senhaErrors"
                  v-touch="geral.senha"
                  :append-icon="passwordIcon('senha')"
                  label="Senha"
                  @click:append="
            hiddenPassword.senha = !hiddenPassword.senha
          "
                  hint="Atenção: por questões de segurança, o usuário deve mudar a senha no primeiro acesso."
                  persistent-hint
                />
                <v-text-field
                  v-if="alterarSenha"
                  :type="passwordType('confirmacao_senha')"
                  v-model="geral.confirmacao_senha"
                  :error-messages="confirmacaoSenhaErrors"
                  v-touch="geral.confirmacao_senha"
                  :append-icon="passwordIcon('confirmacao_senha')"
                  label="Confirme a senha"
                  @click:append="
            hiddenPassword.confirmacao_senha = !hiddenPassword.confirmacao_senha
          "
                />
              </template>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-treeview selectable :items="permissoes" open-on-click v-model="permSelecionadas" />
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-select
                v-model="empresaSelecionada"
                :items="empresasNaoAssociadas"
                label="Associar empresa"
                append-outer-icon="mdi mdi-plus"
                @click:append-outer="associar()"
                item-value="id"
              >
                <template v-slot:item="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{item.cnpj}}</v-list-item-subtitle>
                    <v-list-item-title>{{item.razao_social}}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{item.cnpj}}</v-list-item-subtitle>
                    <v-list-item-title>{{item.razao_social}}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
              <v-list two-line flat subheader>
                <v-subheader>Empresas associadas</v-subheader>
                <template v-if="empresasAssociadas.length > 0">
                  <v-list-item v-for="(emp, i) in empresasAssociadas" :key="`empresa-${i}`">
                    <v-list-item-content>
                      <v-list-item-subtitle>{{emp.cnpj}}</v-list-item-subtitle>
                      <v-list-item-title>{{emp.razao_social}}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click="desassociar(emp)" title="Desassociar empresa">
                        <v-icon class="mdi mdi-delete"></v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <v-list-item v-else>
                  <v-list-item-content>
                    <v-list-item-title class="text-center">Nenhuma empresa associada</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-select
                :items="empresasAssociadas"
                v-model="empresaPadrao"
                item-value="id"
                label="Empresa padrão"
                hint="A empresa padrão determina qual será a empresa ativa após login"
                persistent-hint
                v-if="empresasAssociadas.length > 0"
              >
                <template v-slot:item="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{item.cnpj}}</v-list-item-subtitle>
                    <v-list-item-title>{{item.razao_social}}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{item.cnpj}}</v-list-item-subtitle>
                    <v-list-item-title>{{item.razao_social}}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
        <v-card-actions>
          <v-btn depressed color="primary" @click="submit" :loading="loading">Salvar</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-container>
</template>

<script>
import { required, maxLength, email, sameAs } from "vuelidate/lib/validators";
import _ from "lodash";
import { mapState } from "vuex";

const padrao = {
  id: "",
  login: "",
  nome: "",
  email: "",
  ativo: true,
  senha: "",
  confirmacao_senha: "",
  id_funcionario: null
};
export default {
  data() {
    return {
      geral: _.clone(padrao),
      loading: false,
      tab: null,
      hiddenPassword: {
        senha: true,
        confirmacao_senha: true
      },
      permissoes: [],
      permSelecionadas: [],
      alterarSenha: false,
      debounce: null,
      empresaSelecionada: null,
      empresasAssociadas: [],
      empresas: [],
      empresaPadrao: null,
      pesquisa: null,
      pesquisando: false
    };
  },
  validations() {
    const regras = {
      geral: {
        login: { required, maxLength: maxLength(30) },
        nome: { required, maxLength: maxLength(80) },
        email: { required, email, maxLength: maxLength(128) },
        senha: {},
        confirmacao_senha: {}
      }
    };

    if (this.geral.id === "" || this.alterarSenha) {
      regras.geral = {
        ...regras.geral,
        ...{
          senha: { required },
          confirmacao_senha: {
            igualSenha: sameAs("senha")
          }
        }
      };
      if (this.geral.id === "") {
        regras.geral.login.loginNaoExiste = value =>
          this.$store.dispatch("usuarios/checar", value);
      }
    }

    return regras;
  },
  computed: {
    empresasNaoAssociadas() {
      const vm = this;
      return this.empresas.filter(
        e => e.ativo && !vm.empresasAssociadas.find(ea => ea.id === e.id)
      );
    },
    loginErrors() {
      const errors = [];
      if (!this.$v.geral.login.$dirty) return errors;
      !this.$v.geral.login.required &&
        errors.push("Informe o login do usuário.");
      !this.$v.geral.login.maxLength && errors.push("Somente 30 caracteres.");
      !this.$v.geral.login.loginNaoExiste &&
        errors.push("Este login já existe");
      return errors;
    },
    nomeErrors() {
      const errors = [];
      if (!this.$v.geral.nome.$dirty) return errors;
      !this.$v.geral.nome.required && errors.push("Informe o nome do usuário.");
      !this.$v.geral.nome.maxLength && errors.push("Somente 80 caracteres.");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.geral.email.$dirty) return errors;
      !this.$v.geral.email.required &&
        errors.push("Informe o email do usuário.");
      !this.$v.geral.email.email &&
        errors.push("Informe um email válido para o usuário.");
      !this.$v.geral.email.maxLength && errors.push("Somente 128 caracteres.");
      return errors;
    },
    senhaErrors() {
      const errors = [];
      if (!this.$v.geral.senha.$dirty) return errors;
      !this.$v.geral.senha.required &&
        errors.push("Informe a senha do usuário.");
      return errors;
    },
    confirmacaoSenhaErrors() {
      const errors = [];
      if (!this.$v.geral.confirmacao_senha.$dirty) return errors;
      !this.$v.geral.confirmacao_senha.igualSenha &&
        errors.push("A confirmação de senha falhou.");
      return errors;
    },
    ...mapState({
      funcionarios: state => state.funcionario.Funcionarios
    })
  },
  async mounted() {
    this.loading = true;

    //carregar permissões para montar árvore
    try {
      const grupos = {};
      const response = await this.$store.dispatch("permissao/listar", {});
      for (let perm of response.data) {
        if (!grupos[perm.grupo]) {
          grupos[perm.grupo] = [];
        }
        grupos[perm.grupo].push(perm);
      }
      const arvore = [];
      Object.keys(grupos)
        .sort()
        .forEach((key, i) => {
          arvore.push({
            id: `grupo-${i}`,
            name: key,
            children: grupos[key].map(perm => ({
              id: perm.nome,
              name: perm.descricao
            }))
          });
        });

      this.permissoes = arvore;
    } catch (e) {
      console.log(e);
    }

    //carregar empresas
    try {
      const response = await this.$store.dispatch("empresa/listar");
      this.empresas = response.data;
    } catch (e) {
      console.log(e);
    }

    //carregar usuário
    if (this.$route.params.id) {
      try {
        const response = await this.$store.dispatch(
          "usuarios/carregarUsuario",
          { id: this.$route.params.id, filtros: { carregar_tudo: true } }
        );
        this.geral = {
          id: response.data.id,
          login: response.data.login,
          nome: response.data.nome,
          email: response.data.email,
          ativo: response.data.ativo,
          id_funcionario: response.data.id_funcionario
        };

        let funcionario = null;
        if (response.data.id_funcionario !== null) {
          const responseFuncionario = await this.$store.dispatch(
            "funcionario/carregarFuncionario",
            response.data.id_funcionario
          );
          funcionario = responseFuncionario.data;
          this.$store.commit("funcionario/carregar", [funcionario]);
        }

        this.permSelecionadas = response.data.permissoes;
        this.empresasAssociadas = response.data.empresas;
        this.empresaPadrao = response.data.id_empresa_padrao;
      } catch (e) {
        console.log(e);
      }
    }

    this.loading = false;
  },
  methods: {
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;
        try {
          const vm = this;
          await this.$store.dispatch("usuarios/gravar", {
            usuario: this.geral,
            permissoes: this.permSelecionadas,
            empresas: this.empresasAssociadas.map(ea => ({
              id_empresa: ea.id,
              padrao: ea.id == vm.empresaPadrao
            }))
          });
          this.$router.replace({ name: "usuarios-list" });
        } catch (e) {
          console.log(e);
        }
        this.loading = false;
      }
    },
    passwordType(field) {
      return this.hiddenPassword[field] ? "password" : "text";
    },
    passwordIcon(field) {
      return this.hiddenPassword[field] ? "mdi mdi-eye" : "mdi mdi-eye-off";
    },
    associar() {
      const vm = this;
      this.empresasAssociadas.push(
        this.empresas.find(e => e.id === vm.empresaSelecionada)
      );
      if (!this.empresaPadrao) {
        this.empresaPadrao = this.empresaSelecionada;
      }
      this.empresaSelecionada = null;
    },
    desassociar(e) {
      this.empresasAssociadas = this.empresasAssociadas.filter(
        ea => ea.id !== e.id
      );

      if (e.id === this.empresaPadrao) {
        if (this.empresasAssociadas.length > 0) {
          this.empresaPadrao = this.empresasAssociadas[0].id;
        } else {
          this.empresaPadrao = null;
        }
      }
    },
    pesquisarFuncionarios: _.debounce(async function() {
      this.pesquisando = true;
      await this.$store.dispatch("funcionario/listar", {
        pesquisa: this.pesquisa
      });
      this.pesquisando = false;
    }, 300)
  },
  watch: {
    alterarSenha() {
      this.geral.senha = "";
      this.geral.confirmacao_senha = "";
    },
    pesquisa() {
      this.pesquisarFuncionarios();
    }
  }
};
</script>
